/**
 * CopilotIcon — Custom composed icon: bot inside a chat bubble with sparkle accents.
 * Used for the Copilot button/branding throughout the Otto portal.
 *
 * Available in Regular (outline) and Filled variants, matching Fluent icon conventions.
 * Accepts standard SVG props (className, style, aria-hidden, etc.) plus fontSize for sizing.
 */

import type { SVGProps } from 'react';

interface CopilotIconProps extends SVGProps<SVGSVGElement> {
  fontSize?: string | number;
}

/**
 * Regular (outline) variant — chat bubble outline with bot face + sparkle accents.
 */
export const CopilotIconRegular: React.FC<CopilotIconProps> = ({
  fontSize = '1em',
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={fontSize}
    height={fontSize}
    fill="currentColor"
    {...props}
  >
    {/* Chat bubble outline */}
    <path
      d="M5.25 3C3.45 3 2 4.45 2 6.25v8.5C2 16.55 3.45 18 5.25 18H7v2.75a.75.75 0 0 0 1.28.53L11.56 18h7.19c1.8 0 3.25-1.45 3.25-3.25v-8.5C22 4.45 20.55 3 18.75 3H5.25Zm-1.75 3.25c0-1 .8-1.75 1.75-1.75h13.5c1 0 1.75.8 1.75 1.75v8.5c0 1-.8 1.75-1.75 1.75h-7.5a.75.75 0 0 0-.53.22L8.5 17.94V17.25a.75.75 0 0 0-.75-.75h-2.5c-1 0-1.75-.8-1.75-1.75v-8.5Z"
      fillRule="evenodd"
      clipRule="evenodd"
    />
    {/* Bot eyes inside the bubble */}
    <circle cx="9.5" cy="10" r="1.25" />
    <circle cx="14.5" cy="10" r="1.25" />
    {/* Sparkle accent — top right */}
    <path d="M20 1.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4L18 3.5l1.4-.6.6-1.4Z" />
  </svg>
);

/**
 * Filled variant — solid chat bubble with bot face + sparkle accents.
 */
export const CopilotIconFilled: React.FC<CopilotIconProps> = ({
  fontSize = '1em',
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={fontSize}
    height={fontSize}
    fill="currentColor"
    {...props}
  >
    {/* Sparkle accent — top right (rendered first so bubble doesn't overlap) */}
    <path d="M20 1.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4L18 3.5l1.4-.6.6-1.4Z" />
    {/* Solid chat bubble */}
    <path d="M5.25 3C3.45 3 2 4.45 2 6.25v8.5C2 16.55 3.45 18 5.25 18H7v2.75a.75.75 0 0 0 1.28.53L11.56 18h7.19c1.8 0 3.25-1.45 3.25-3.25v-8.5C22 4.45 20.55 3 18.75 3H5.25Z" />
    {/* Bot eyes (inverted/cutout via white fill) */}
    <circle cx="9.5" cy="10" r="1.25" fill="var(--copilot-icon-eye, white)" />
    <circle cx="14.5" cy="10" r="1.25" fill="var(--copilot-icon-eye, white)" />
  </svg>
);

/**
 * CopilotLogoIcon — Official Copilot brand mark.
 * Uses `currentColor` so it inherits its color from the parent button —
 * matching the color/hover/active behavior of the surrounding toolbar icons.
 */
export const CopilotLogoIcon: React.FC<CopilotIconProps> = ({
  fontSize = '1em',
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    width={fontSize}
    height={fontSize}
    fill="none"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path
      d="M6.77889 14.9999H11.5938C12.6162 14.9999 13.3728 14.3436 13.9211 13.5283C14.4714 12.71 14.8793 11.638 15.1961 10.5875C15.5592 9.38351 16.0165 7.86674 15.9995 6.64477C15.991 6.02748 15.8622 5.40417 15.4636 4.93124C15.0503 4.44087 14.4256 4.2026 13.6254 4.2026H13.1916C12.8184 4.17148 12.4946 3.92276 12.3697 3.56318L11.936 2.31371C11.663 1.52708 10.9239 1 10.0939 1H9.23663L9.22114 1.00006H4.4062C3.38378 1.00006 2.62719 1.65641 2.07891 2.47169C1.52859 3.29001 1.12069 4.36198 0.803872 5.41253C0.440784 6.61649 -0.0164514 8.13326 0.000455415 9.35523C0.00899617 9.97253 0.137844 10.5958 0.536424 11.0688C0.949698 11.5591 1.57443 11.7974 2.37461 11.7974H2.8084C3.18159 11.8285 3.50544 12.0772 3.63026 12.4368L4.06399 13.6863C4.33704 14.4729 5.07612 15 5.90608 15H6.76338C6.76855 15 6.77372 15 6.77889 14.9999ZM7.77534 6.65792L8.0257 5.87203C8.15212 5.4752 8.51962 5.20589 8.93472 5.20589H9.46421C9.3917 5.33608 9.33322 5.47609 9.29105 5.62418C9.02949 6.54282 8.62995 7.94131 8.22459 9.34234L7.97431 10.128C7.84789 10.5248 7.48039 10.7941 7.06529 10.7941H6.53579C6.6083 10.6639 6.66678 10.5239 6.70895 10.3758C6.9705 9.45723 7.37 8.05887 7.77534 6.65792ZM7.52454 11.7392C7.39824 12.1671 7.2841 12.5504 7.16311 12.8883C7.02668 13.2694 6.89317 13.5582 6.74854 13.7632C6.66221 13.8855 6.49575 13.9999 6.12413 13.9999C6.12151 13.9999 6.11889 14 6.11627 14H5.90608C5.50003 14 5.13845 13.7421 5.00486 13.3573L4.57113 12.1078C4.53341 11.9992 4.4868 11.8954 4.43229 11.7974H4.8318C4.8699 11.7974 4.9078 11.7963 4.94546 11.7941L7.06529 11.7941C7.22255 11.7941 7.37648 11.7752 7.52454 11.7392ZM11.4289 3.89218C11.4666 4.00084 11.5132 4.10455 11.5677 4.2026H11.1682C11.1301 4.2026 11.0922 4.20371 11.0545 4.2059L8.93472 4.2059C8.77745 4.2059 8.62353 4.22481 8.47546 4.26079C8.60176 3.83287 8.7159 3.4496 8.8369 3.11166C8.97332 2.73061 9.10684 2.44177 9.25146 2.23683C9.33779 2.11449 9.50425 2.00005 9.87587 2.00005L9.88372 1.99999H10.0939C10.5 1.99999 10.8616 2.25786 10.9952 2.64271L11.4289 3.89218ZM1.75748 5.70223C2.06577 4.67999 2.43925 3.7233 2.90473 3.03115C3.37224 2.33597 3.86586 2.00005 4.4062 2.00005H8.23064C8.10425 2.23835 7.99671 2.5013 7.89926 2.77348C7.76184 3.15729 7.63428 3.58978 7.50273 4.03582L7.47356 4.1347C6.89556 6.09246 6.15673 8.67597 5.75096 10.101C5.63358 10.5133 5.25841 10.7974 4.8318 10.7974H2.37461C1.77299 10.7974 1.46751 10.6251 1.29712 10.423C1.11203 10.2033 1.00376 9.85209 0.996696 9.34134C0.982388 8.30722 1.37998 6.95398 1.75748 5.70223ZM13.0953 12.9689C12.6278 13.664 12.1341 13.9999 11.5938 13.9999H7.76936C7.89575 13.7617 8.00329 13.4987 8.10074 13.2265C8.23816 12.8427 8.36572 12.4102 8.49727 11.9642L8.52645 11.8653C9.10445 9.90754 9.84327 7.32403 10.249 5.89895C10.3664 5.48671 10.7416 5.20259 11.1682 5.20259H13.6254C14.227 5.20259 14.5325 5.37487 14.7029 5.57705C14.888 5.79666 14.9962 6.14792 15.0033 6.65866C15.0176 7.69278 14.62 9.04602 14.2425 10.2978C13.9342 11.32 13.5608 12.2767 13.0953 12.9689Z"
      fill="currentColor"
    />
  </svg>
);
